import { ActivityParentEntityId, CustomListKey } from "@sp-crm/core";
import { ActivityFeed } from "components/activity-feed/activity-feed";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { ContentFull, ContentSection2Up } from "components/layout";
import { ActivityType, useGetActivityFeedQuery } from "generated/graphql";
import React from "react";
import { EntityActivityClientStatus } from "./entity-activity-client-status";
import {
    EntityActivityCollaboratorAdded,
    EntityActivityCollaboratorRemoved,
} from "./entity-activity-collaborator";
import {
    EntityActivityEntityCreate,
    EntityActivityEntityDeleted,
    EntityActivityEntityRestored,
} from "./entity-activity-entity";
import { EntityActivityEntryFileUpload } from "./entity-activity-file";
import {
    EntityActivityMessageSent,
    EntityActivitySmsSent,
} from "./entity-activity-message";
import {
    EntityActivityEntryNoteAdd,
    EntityActivityEntryNoteDelete,
} from "./entity-activity-note";
import {
    EntityActivityOrganizationMemberAdded,
    EntityActivityOrganizationMemberRemoved,
} from "./entity-activity-organization";
import {
    EntityActivityPipelineCardAdded,
    EntityActivityPipelineCardMoved,
    EntityActivityPipelineCardRemoved,
} from "./entity-activity-pipeline-card";
import { EntityActivityReassign } from "./entity-activity-reassign";
import {
    EntityActivityTaskComplete,
    EntityActivityTaskCreate,
    EntityActivityTaskDelete,
    EntityActivityTaskReassign,
    EntityActivityTaskUncomplete,
    EntityActivityTaskUpdate,
} from "./entity-activity-task";
import {
    EntityActivityTouchpointLogged,
    EntityActivityTouchpointSnoozed,
    EntityActivityVisitScheduled,
} from "./entity-activity-touchpoint";
import { EntityActivityVoiceCall } from "./entity-activity-voice-call";
import { ActivityEntityType, EntityActivityEntry } from "./types";

interface ActivityProps {
    entityId: ActivityParentEntityId;
    entityType: ActivityEntityType;
}

export const Activity: React.FC<ActivityProps> = props => {
    const { entityId, entityType } = props;
    const query = useGetActivityFeedQuery({ entityId });
    return (
        <ContentSection2Up className="mx-0!">
            <div className="flex-1 bg-white border border-gray-200 rounded-lg shadow-sm">
                <ContentFull>
                    <QueryRenderer name="EntityActivity" query={query}>
                        {data => (
                            <ActivityFeed>
                                {data.getActivityFeed.activities
                                    .filter(e => e.activity.parentEntityId === entityId)
                                    .map(e =>
                                        entityActivityEntry(entityId, entityType, e),
                                    )}
                            </ActivityFeed>
                        )}
                    </QueryRenderer>
                </ContentFull>
            </div>
            <div className="flex-1"></div>
        </ContentSection2Up>
    );
};

const entityActivityEntry = (
    entityId: ActivityParentEntityId,
    entityType: ActivityEntityType,
    activityFeedEvent: EntityActivityEntry,
): JSX.Element | null => {
    const activityType = activityFeedEvent.activity.activityType;
    if (activityType === ActivityType.NoteCreate) {
        return (
            <EntityActivityEntryNoteAdd
                key={activityFeedEvent.activity.id}
                entityId={entityId}
                activity={activityFeedEvent}
            />
        );
    }
    if (activityType === ActivityType.NoteUpdate) {
        return null;
    }
    if (activityType === ActivityType.NoteDelete) {
        return (
            <EntityActivityEntryNoteDelete
                key={activityFeedEvent.activity.id}
                entityId={entityId}
                activity={activityFeedEvent}
            />
        );
    }
    if (activityType === ActivityType.TaskCreate) {
        return (
            <EntityActivityTaskCreate
                key={activityFeedEvent.activity.id}
                entityId={entityId}
                activity={activityFeedEvent}
            />
        );
    }
    if (activityType === ActivityType.EntityFieldChange) {
        if (
            (entityType === "Client" || entityType === "HomeCareLead") &&
            activityFeedEvent.activity.metadata?.events?.some(
                e =>
                    e.entityFieldChange?.field === "status" ||
                    e.entityFieldChange?.field === "statusListItemId",
            )
        ) {
            const customListKey =
                entityType === "Client"
                    ? CustomListKey.ClientStatus
                    : CustomListKey.HomeCareLeadStatus;
            return (
                <EntityActivityClientStatus
                    key={activityFeedEvent.activity.id}
                    entityId={entityId}
                    activity={activityFeedEvent}
                    customListKey={customListKey}
                />
            );
        }
        return null;
    }
    if (activityType === ActivityType.EntityCreate) {
        return (
            <EntityActivityEntityCreate
                key={activityFeedEvent.activity.id}
                entityId={entityId}
                entityType={entityType}
                activity={activityFeedEvent}
            />
        );
    }
    if (activityType === ActivityType.TaskUpdate) {
        return (
            <EntityActivityTaskUpdate
                key={activityFeedEvent.activity.id}
                entityId={entityId}
                activity={activityFeedEvent}
            />
        );
    }
    if (activityType === ActivityType.TaskDelete) {
        return (
            <EntityActivityTaskDelete
                key={activityFeedEvent.activity.id}
                entityId={entityId}
                activity={activityFeedEvent}
            />
        );
    }
    if (activityType === ActivityType.TaskComplete) {
        return (
            <EntityActivityTaskComplete
                key={activityFeedEvent.activity.id}
                entityId={entityId}
                activity={activityFeedEvent}
            />
        );
    }
    if (activityType === ActivityType.TaskUncomplete) {
        return (
            <EntityActivityTaskUncomplete
                key={activityFeedEvent.activity.id}
                entityId={entityId}
                activity={activityFeedEvent}
            />
        );
    }
    if (activityType === ActivityType.TaskReassign) {
        return (
            <EntityActivityTaskReassign
                key={activityFeedEvent.activity.id}
                entityId={entityId}
                activity={activityFeedEvent}
            />
        );
    }
    if (activityType === ActivityType.FileUpload) {
        return (
            <EntityActivityEntryFileUpload
                key={activityFeedEvent.activity.id}
                entityId={entityId}
                activity={activityFeedEvent}
            />
        );
    }
    if (activityType === ActivityType.MessageSent) {
        return (
            <EntityActivityMessageSent
                key={activityFeedEvent.activity.id}
                entityId={entityId}
                activity={activityFeedEvent}
            />
        );
    }

    if (activityType === ActivityType.OutgoingCall) {
        return (
            <EntityActivityVoiceCall
                key={activityFeedEvent.activity.id}
                entityId={entityId}
                activity={activityFeedEvent}
            />
        );
    }

    if (activityType === ActivityType.SmsSent) {
        return (
            <EntityActivitySmsSent
                key={activityFeedEvent.activity.id}
                entityId={entityId}
                activity={activityFeedEvent}
            />
        );
    }

    if (activityType === ActivityType.OrganizationMemberAdded) {
        return (
            <EntityActivityOrganizationMemberAdded
                key={activityFeedEvent.activity.id}
                entityId={entityId}
                activity={activityFeedEvent}
            />
        );
    }

    if (activityType === ActivityType.OrganizationMemberRemoved) {
        return (
            <EntityActivityOrganizationMemberRemoved
                key={activityFeedEvent.activity.id}
                entityId={entityId}
                activity={activityFeedEvent}
            />
        );
    }

    if (activityType === ActivityType.PipelineCardAdded) {
        return (
            <EntityActivityPipelineCardAdded
                key={activityFeedEvent.activity.id}
                entityId={entityId}
                entityType={entityType}
                activity={activityFeedEvent}
            />
        );
    }

    if (activityType === ActivityType.PipelineCardMoved) {
        return (
            <EntityActivityPipelineCardMoved
                key={activityFeedEvent.activity.id}
                entityId={entityId}
                entityType={entityType}
                activity={activityFeedEvent}
            />
        );
    }

    if (activityType === ActivityType.PipelineCardRemoved) {
        return (
            <EntityActivityPipelineCardRemoved
                key={activityFeedEvent.activity.id}
                entityId={entityId}
                entityType={entityType}
                activity={activityFeedEvent}
            />
        );
    }

    if (activityType === ActivityType.EntityReassign) {
        return (
            <EntityActivityReassign
                key={activityFeedEvent.activity.id}
                entityId={entityId}
                activity={activityFeedEvent}
            />
        );
    }

    if (activityType === ActivityType.EntityCollaboratorAdded) {
        return (
            <EntityActivityCollaboratorAdded
                key={activityFeedEvent.activity.id}
                entityId={entityId}
                activity={activityFeedEvent}
            />
        );
    }

    if (activityType === ActivityType.EntityCollaboratorRemoved) {
        return (
            <EntityActivityCollaboratorRemoved
                key={activityFeedEvent.activity.id}
                entityId={entityId}
                activity={activityFeedEvent}
            />
        );
    }

    if (activityType === ActivityType.CommunityRestored) {
        return (
            <EntityActivityEntityRestored
                key={activityFeedEvent.activity.id}
                entityId={entityId}
                entityType={entityType}
                activity={activityFeedEvent}
            />
        );
    }

    if (activityType === ActivityType.CommunityDeleted) {
        return (
            <EntityActivityEntityDeleted
                key={activityFeedEvent.activity.id}
                entityId={entityId}
                entityType={entityType}
                activity={activityFeedEvent}
            />
        );
    }

    if (activityType === ActivityType.TouchpointLogged) {
        return (
            <EntityActivityTouchpointLogged
                key={activityFeedEvent.activity.id}
                entityId={entityId}
                activity={activityFeedEvent}
            />
        );
    }

    if (activityType === ActivityType.TouchpointSnoozed) {
        return (
            <EntityActivityTouchpointSnoozed
                key={activityFeedEvent.activity.id}
                entityId={entityId}
                activity={activityFeedEvent}
            />
        );
    }

    if (activityType === ActivityType.VisitScheduled) {
        return (
            <EntityActivityVisitScheduled
                key={activityFeedEvent.activity.id}
                entityId={entityId}
                activity={activityFeedEvent}
            />
        );
    }

    const exhaustiveCheck: never = activityType;
    throw new Error(`Unhandled activity type: ${exhaustiveCheck}`);
};
